import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Music, Upload, CheckCircle2, XCircle, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";

// NEO — Bulk Audio Intake (locked scope). Sits alongside Bulk Metadata
// Intake. One audio file → one existing-style Kandake track record with the
// normal RT-#### id, filename-seeded working title (editable before
// submission), attached audio, selected Catalog Code, and the standard
// intake/incomplete state. Nothing here grants any downstream eligibility.

const ALLOWED_EXT = ["wav", "mp3", "ogg", "flac", "aac", "m4a", "mp4"];

function newBatchId() {
  return (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
}

function stripExt(name) {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(0, i) : name;
}

export function BulkAudioIntake({ codes, initialDestination = "catalog" }) {
  const [selectedCode, setSelectedCode] = useState("");
  // Destination mode reuses the existing asset-pathway routing:
  // catalog → DEVELOPMENT_CANDIDATE (default) · demo → DEMO_ONLY.
  const [destination, setDestination] = useState(initialDestination === "demo" ? "demo" : "catalog");
  useEffect(() => {
    setDestination(initialDestination === "demo" ? "demo" : "catalog");
  }, [initialDestination]);
  const [items, setItems] = useState([]); // { file, working_title }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  // Idempotency key — regenerated only after a batch completes or the file
  // list changes, so an accidental retry of the SAME submission cannot
  // create duplicate records.
  const batchIdRef = useRef(newBatchId());

  const activeCodes = useMemo(() => (codes || []).filter(c => c.is_active !== false), [codes]);

  function addFiles(fileList) {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setResult(null);
    batchIdRef.current = newBatchId();
    setItems(prev => {
      const existingKeys = new Set(prev.map(x => `${x.file.name}|${x.file.size}`));
      const next = [...prev];
      for (const f of incoming) {
        const ext = (f.name.split(".").pop() || "").toLowerCase();
        if (!ALLOWED_EXT.includes(ext)) {
          toast.error(`${f.name}: unsupported type (.${ext || "?"}) — allowed: ${ALLOWED_EXT.join(", ")}`);
          continue;
        }
        const key = `${f.name}|${f.size}`;
        if (existingKeys.has(key)) continue; // avoid accidental duplicates in the list
        existingKeys.add(key);
        next.push({ file: f, working_title: stripExt(f.name) });
      }
      return next;
    });
  }

  function removeItem(idx) {
    setResult(null);
    batchIdRef.current = newBatchId();
    setItems(prev => prev.filter((_, i) => i !== idx));
  }

  function setTitle(idx, v) {
    setItems(prev => prev.map((x, i) => (i === idx ? { ...x, working_title: v } : x)));
  }

  async function submit() {
    if (!selectedCode) { toast.error("Select a Catalog Code first"); return; }
    if (!items.length) { toast.error("Add at least one audio file"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("catalog_code", selectedCode);
      fd.append("client_batch_id", batchIdRef.current);
      fd.append("mode", destination);
      fd.append("titles", JSON.stringify(items.map(x => x.working_title || "")));
      items.forEach(x => fd.append("files", x.file));
      const r = await api.post("/intake/audio-batch", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setResult(r.data);
      if (r.data.duplicate_retry) {
        toast.info("This batch was already imported — showing the original result (no duplicates created).");
      } else if (r.data.failed_count > 0 && r.data.created_count > 0) {
        toast.warning(`${r.data.created_count} imported · ${r.data.failed_count} failed`);
      } else if (r.data.failed_count > 0) {
        toast.error("All files failed to import");
      } else {
        toast.success(`${r.data.created_count} track${r.data.created_count === 1 ? "" : "s"} created`);
      }
      setItems([]);
      batchIdRef.current = newBatchId();
    } catch (e) {
      toast.error(errMsg(e, "Bulk audio intake failed"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="bulk-audio-intake">
      {/* Header */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
          Catalog · Intake
        </div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight flex items-center gap-3">
          <Music className="w-7 h-7" style={{ color: "hsl(347 60% 22%)" }} />
          Bulk Audio Intake
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Upload multiple audio files at once. Each file becomes one Kandake track in the
          standard <span className="font-medium text-foreground">intake / Incomplete</span> state —
          compliance, documents and eligibility are handled later through the normal gates.
        </p>
      </div>

      {/* Setup */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">1. Select Catalog Code and add audio files</CardTitle>
          <CardDescription>
            Every track in the batch is assigned the chosen Catalog Code. Allowed types: {ALLOWED_EXT.join(", ")} · max 100 MB per file · max 50 files per batch.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex flex-wrap gap-5">
            <div className="max-w-sm min-w-[260px] flex-1 space-y-1.5">
              <Label htmlFor="audio-cc-pick">Catalog Code *</Label>
            <Select value={selectedCode} onValueChange={setSelectedCode}>
              <SelectTrigger id="audio-cc-pick" data-testid="audio-intake-catalog-code-select">
                <SelectValue placeholder="Select a Catalog Code…" />
              </SelectTrigger>
              <SelectContent>
                {activeCodes.map(c => (
                  <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeCodes.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No active Catalog Codes. <Link to="/catalog-codes" className="underline">Open registry</Link>.
              </p>
            )}
            </div>
            <div className="max-w-xs min-w-[220px] space-y-1.5">
              <Label htmlFor="audio-dest-pick">Destination mode *</Label>
              <Select value={destination} onValueChange={setDestination}>
                <SelectTrigger id="audio-dest-pick" data-testid="audio-intake-destination-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="catalog">Catalog mode — standard catalog tracks</SelectItem>
                  <SelectItem value="demo">Demo mode — DEMO_ONLY pathway tracks</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {selectedCode === "DFR"
                  ? "DFR — Demo For Review: this designation is authoritative. Files route to the Demo Register regardless of destination mode."
                  : destination === "demo"
                  ? "Tracks are created on the existing DEMO_ONLY pathway (blocked from downstream elevation)."
                  : "Tracks are created on the standard development pathway."}
              </p>
            </div>
          </div>

          {/* Drop zone */}
          <div
            role="button"
            tabIndex={0}
            aria-label="Add audio files"
            data-testid="audio-intake-dropzone"
            className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${dragOver ? "border-[hsl(347_60%_35%)] bg-secondary/40" : "border-border hover:bg-secondary/30"}`}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          >
            <Upload className="w-7 h-7 mx-auto mb-2 text-muted-foreground" />
            <div className="text-sm font-medium">Drag &amp; drop audio files here, or click to browse</div>
            <div className="text-xs text-muted-foreground mt-1">You can add multiple files in one batch.</div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="audio/*,.wav,.mp3,.ogg,.flac,.aac,.m4a,.mp4"
              className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              data-testid="audio-intake-file-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* File list with editable working titles */}
      {items.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">2. Review working titles</CardTitle>
            <CardDescription>
              Working title defaults to the filename (minus extension). Edit before importing if needed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Working title</TableHead>
                  <TableHead className="w-[110px]">Size</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((x, i) => (
                  <TableRow key={`${x.file.name}-${i}`} data-testid={`audio-intake-row-${i}`}>
                    <TableCell className="text-xs font-mono text-muted-foreground max-w-[240px] truncate">{x.file.name}</TableCell>
                    <TableCell>
                      <Input
                        value={x.working_title}
                        onChange={(e) => setTitle(i, e.target.value)}
                        maxLength={300}
                        data-testid={`audio-intake-title-input-${i}`}
                      />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{(x.file.size / (1024 * 1024)).toFixed(1)} MB</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeItem(i)} data-testid={`audio-intake-remove-${i}`} aria-label={`Remove ${x.file.name}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center gap-3">
              <Button
                onClick={submit}
                disabled={submitting || !selectedCode || items.length === 0}
                style={{ background: "hsl(347 72% 22%)" }}
                data-testid="audio-intake-submit-btn"
              >
                <Inbox className="w-4 h-4 mr-2" />
                {submitting ? "Importing…" : `Import ${items.length} file${items.length === 1 ? "" : "s"}`}
              </Button>
              <span className="text-xs text-muted-foreground">
                Each file creates one track in the intake / Incomplete state.
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results summary */}
      {result && (
        <Card data-testid="audio-intake-results">
          <CardHeader>
            <CardTitle className="text-lg">Batch results</CardTitle>
            <CardDescription>
              {result.created_count} created · {result.failed_count} failed · Catalog Code {result.catalog_code}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {result.created?.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kandake ID</TableHead>
                    <TableHead>Working title</TableHead>
                    <TableHead>Source file</TableHead>
                    <TableHead className="w-[110px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.created.map(c => (
                    <TableRow key={c.id} data-testid={`audio-intake-result-row-${c.track_id}`}>
                      <TableCell className="font-mono text-sm flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4" style={{ color: "hsl(140 45% 38%)" }} />
                        {c.track_id}
                      </TableCell>
                      <TableCell className="font-medium text-sm">{c.working_title}</TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground max-w-[220px] truncate">{c.filename}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" asChild data-testid={`audio-intake-open-track-${c.track_id}`}>
                          <Link to={`/tracks/${c.id}`}>Open track</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {result.failed?.length > 0 && (
              <Alert variant="destructive" data-testid="audio-intake-failures">
                <XCircle className="w-4 h-4" />
                <AlertDescription>
                  <div className="font-medium mb-1">Failed files</div>
                  {result.failed.map((f, i) => (
                    <div key={i} className="text-xs">{f.filename} — {f.reason}</div>
                  ))}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
